import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AdvShell } from "@/components/AdvShell";
import { money } from "./advUtils";
import { toast } from "sonner";
import { CreditCard, Plus, AlertTriangle, RefreshCw } from "lucide-react";

export default function AdvBilling() {
  const [params, setParams] = useSearchParams();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/adv/billing").then((r) => setB(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // finalize card setup on return from Stripe
  useEffect(() => {
    const s = params.get("setup_session");
    if (s) {
      api.post("/adv/billing/setup/complete", { session_id: s })
        .then((r) => { toast.success(`Card added: ${r.data.brand} ••${r.data.last4}`); load(); })
        .catch((e) => toast.error(apiError(e.response?.data?.detail)))
        .finally(() => setParams({}));
    } else if (params.get("setup") === "cancel") { toast.info("Card setup cancelled"); setParams({}); }
  }, []);

  const addCard = async () => {
    setBusy(true);
    try { const { data } = await api.post("/adv/billing/setup", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };
  const retry = async () => {
    setBusy(true);
    try { await api.post("/adv/billing/retry"); toast.success("Payment successful — campaigns can resume"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <AdvShell>
      <h1 className="font-heading text-4xl tracking-tight mb-5" data-testid="adv-billing-title">Billing</h1>

      {b?.account_status === "past_due" && (
        <div className="mb-6 rounded-lg border border-race-red/50 bg-race-red/10 p-5" data-testid="adv-billing-pastdue">
          <div className="flex items-center gap-2 font-semibold text-race-red mb-1"><AlertTriangle size={18} /> Weekly payment failed</div>
          <p className="text-sm text-race-sub">Outstanding balance: <b className="text-race-txt">{money(b.outstanding_unbilled)}</b>. Your campaigns are paused. Update your card or retry the payment to resume.</p>
          <div className="flex gap-3 mt-3">
            <button onClick={retry} disabled={busy} className="bg-race-volt text-race-bg font-semibold text-sm px-4 py-2 rounded-md disabled:opacity-50" data-testid="adv-retry-payment">{busy ? "…" : "Retry Payment"}</button>
            <button onClick={addCard} disabled={busy} className="border border-race-line text-sm px-4 py-2 rounded-md hover:border-race-volt">Update Payment Method</button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mb-8">
        <div className="bg-race-surface border border-race-line rounded-lg p-5" data-testid="adv-payment-method">
          <div className="text-sm font-semibold mb-3">Payment method</div>
          {b?.has_payment_method ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3"><CreditCard size={22} className="text-race-volt" /><span className="capitalize">{b.payment_brand} ending in {b.payment_last4}</span></div>
              <button onClick={addCard} disabled={busy} className="text-xs text-race-volt hover:underline" data-testid="adv-change-card">Change</button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-race-sub mb-3">Add a card to submit and run campaigns. No upfront charge — you're billed weekly for delivered spend.</p>
              <button onClick={addCard} disabled={busy} data-testid="adv-add-card" className="inline-flex items-center gap-2 bg-race-volt text-race-bg font-semibold text-sm px-4 py-2 rounded-md disabled:opacity-50">{busy ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={16} />} Add Payment Method</button>
            </div>
          )}
        </div>
        <div className="bg-race-surface border border-race-line rounded-lg p-5">
          <div className="text-sm font-semibold mb-3">Current unbilled spend</div>
          <div className="font-heading text-3xl">{money(b?.outstanding_unbilled)}</div>
          <p className="text-xs text-race-sub mt-1">Delivered ad spend not yet charged. Collected automatically at your next weekly billing or when it reaches the billing threshold.</p>
        </div>
      </div>

      <h2 className="font-heading text-xl tracking-tight mb-3">Charges & receipts</h2>
      {(!b?.charges || b.charges.length === 0) ? (
        <div className="rounded-lg border border-dashed border-race-line py-10 text-center text-race-sub" data-testid="adv-no-charges">No charges yet.</div>
      ) : (
        <div className="space-y-2" data-testid="adv-charges">
          {b.charges.map((ch) => (
            <div key={ch.id} className="bg-race-surface border border-race-line rounded-lg p-4 flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold">{money(ch.amount)} <span className="text-xs font-normal text-race-sub">· {ch.reason}</span></div>
                <div className="text-xs text-race-muted">{(ch.created_at || "").slice(0, 16).replace("T", " ")} · {ch.allocations?.length || 0} campaign(s)</div>
                {ch.error && <div className="text-xs text-race-red mt-1">{ch.error}</div>}
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${ch.status === "succeeded" ? "bg-emerald-500/15 text-emerald-300" : "bg-race-red/15 text-race-red"}`}>{ch.status}</span>
            </div>
          ))}
        </div>
      )}
    </AdvShell>
  );
}
